import { useEffect, useMemo, useRef, useState } from 'react';
import Footer from '../../components/feature/Footer';
import ScrollToTop from '../../components/feature/ScrollToTop';
import LoadingSpinner from '../../components/feature/LoadingSpinner';
import BackToHome from '../../components/feature/BackToHome';
import RevealStagger from '../../components/feature/RevealStagger';
import FaqCard from '../../components/feature/FaqCard';
import Seo, { buildBreadcrumbJsonLd, SITE_URL } from '../../components/feature/Seo';
import {
  fetchAllFaqs,
  fetchFaqSeite,
  FAQ_CATEGORY_LABELS,
  FAQ_CATEGORY_ORDER,
  type Faq,
  type FaqCategory,
  type FaqSeite,
} from '../../services/faq';

function stripMarkdown(input: string): string {
  return input
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[#>_~]/g, '')
    .replace(/\n+/g, ' ')
    .trim();
}

function categorySectionId(cat: FaqCategory): string {
  return `kategorie-${cat}`;
}

export default function FaqPage() {
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [seite, setSeite] = useState<FaqSeite | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<FaqCategory | null>(null);
  const isPillClicking = useRef(false);
  const pillsScrollerRef = useRef<HTMLDivElement>(null);
  const pillRefs = useRef<Partial<Record<FaqCategory, HTMLButtonElement | null>>>({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const [faqsData, seiteData] = await Promise.all([fetchAllFaqs(), fetchFaqSeite()]);
        setFaqs(faqsData);
        setSeite(seiteData);
      } catch (err) {
        console.error('Failed to load FAQ data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Group + only keep categories that have entries, in canonical order
  const grouped = useMemo(() => {
    const map = new Map<FaqCategory, Faq[]>();
    for (const f of faqs) {
      const list = map.get(f.category) ?? [];
      list.push(f);
      map.set(f.category, list);
    }
    return FAQ_CATEGORY_ORDER.filter((cat) => (map.get(cat)?.length ?? 0) > 0).map((cat) => ({
      category: cat,
      label: FAQ_CATEGORY_LABELS[cat],
      items: map.get(cat) ?? [],
    }));
  }, [faqs]);

  // Set first present category as default active for pill highlight
  useEffect(() => {
    if (!activeCategory && grouped.length > 0) {
      setActiveCategory(grouped[0].category);
    }
  }, [grouped, activeCategory]);

  // Mobile pills auto-scroll: keep active pill visible inside the horizontal scroller
  useEffect(() => {
    if (!activeCategory) return;
    const container = pillsScrollerRef.current;
    const btn = pillRefs.current[activeCategory];
    if (!container || !btn) return;
    const containerRect = container.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const offsetToCenter =
      (btnRect.left - containerRect.left) - containerRect.width / 2 + btnRect.width / 2;
    container.scrollTo({ left: container.scrollLeft + offsetToCenter, behavior: 'smooth' });
  }, [activeCategory]);

  // Scroll-spy for sticky pills
  useEffect(() => {
    if (grouped.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (isPillClicking.current) return;
        const visible = entries.find((e) => e.isIntersecting);
        if (visible) {
          const id = visible.target.id.replace('kategorie-', '') as FaqCategory;
          setActiveCategory(id);
        }
      },
      { rootMargin: '-30% 0px -55% 0px' }
    );
    grouped.forEach(({ category }) => {
      const el = document.getElementById(categorySectionId(category));
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [grouped]);

  const handlePillClick = (cat: FaqCategory) => {
    isPillClicking.current = true;
    setActiveCategory(cat);
    const el = document.getElementById(categorySectionId(cat));
    if (el) {
      // lg+ has no sticky pills bar (sidebar takes over), so smaller offset; below lg account for nav + pills
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      const yOffset = isDesktop ? -96 : -120;
      const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setTimeout(() => {
      isPillClicking.current = false;
    }, 800);
  };

  const seoTitle = seite?.pageTitle
    ? `${seite.pageTitle} | Gerald Fasching`
    : 'Häufig gestellte Fragen | Gerald Fasching';
  const seoDescription =
    seite?.seoDescription ||
    seite?.pageSubheading ||
    'Antworten auf häufige Fragen zu Business Coaching, Personal Coaching, Alchemistischen Kamingesprächen, dem BHS-Modell, Ablauf und Investition - bei Gerald Fasching.';

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${SITE_URL}/faq#faqpage`,
    inLanguage: 'de-AT',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripMarkdown(f.answer),
      },
    })),
  };

  const seo = (
    <Seo
      title={seoTitle}
      description={seoDescription}
      path="/faq"
      jsonLd={[
        faqJsonLd,
        buildBreadcrumbJsonLd([
          { name: 'Start', path: '/' },
          { name: 'FAQ', path: '/faq' },
        ]),
      ]}
    />
  );

  if (loading) {
    return (
      <>
        {seo}
        <LoadingSpinner bgColor="bg-[#FAF8F5]" />
      </>
    );
  }

  const heading = seite?.pageTitle || 'Häufig gestellte Fragen';
  const subtitle = seite?.pageSubheading;
  const badgeLabel = seite?.badgeText || 'Häufige Fragen';

  return (
    <div className="min-h-screen bg-[#FAF8F5] overflow-x-clip">
      {seo}

      {/* HERO ============================================================ */}
      <section className="relative overflow-hidden bg-[#FAF8F5] px-6 pb-12 pt-32 lg:px-12 lg:pb-16 lg:pt-40">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #1A2B3C 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="pointer-events-none absolute -left-32 -top-32 hidden h-[28rem] w-[28rem] rounded-full bg-[#8B1538]/[0.04] blur-3xl lg:block" />
        <div className="pointer-events-none absolute -right-40 top-40 hidden h-[32rem] w-[32rem] rounded-full bg-[#C9A961]/[0.08] blur-3xl lg:block" />

        <div className="relative mx-auto max-w-5xl">
          <BackToHome />

          <RevealStagger animateOnMount className="mx-auto mt-2 max-w-3xl text-center">
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#8B1538]/20 bg-white/70 px-4 py-2 backdrop-blur-sm">
              <i className="ri-question-answer-line flex h-4 w-4 items-center justify-center text-[#8B1538]"></i>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8B1538] lg:text-[13px]">
                {badgeLabel}
              </span>
            </div>

            <h1
              className="mb-6 font-serif text-[1.75rem] font-bold leading-snug tracking-[-0.005em] text-[#1A2B3C] md:text-[2.125rem] lg:text-[2.75rem]"
              style={{ fontFamily: 'Lora, serif' }}
            >
              {heading}
            </h1>

            <div className="mx-auto mb-6 h-px w-12 bg-[#C9A961]" />

            {subtitle && (
              <p className="mx-auto max-w-2xl text-base leading-relaxed text-[#1A2B3C]/75 lg:text-lg">
                {subtitle}
              </p>
            )}
          </RevealStagger>
        </div>
      </section>

      {/* STICKY CATEGORY PILLS (mobile only - sidebar takes over on lg+) */}
      {grouped.length > 1 && (
        <div className="sticky top-[4.5rem] z-30 border-y border-[#1A2B3C]/[0.06] bg-[#FAF8F5]/90 backdrop-blur-md lg:hidden">
          <div
            ref={pillsScrollerRef}
            className="mx-auto flex max-w-5xl items-center gap-2 overflow-x-auto px-6 py-3"
          >
            {grouped.map(({ category, label, items }) => {
              const isActive = activeCategory === category;
              return (
                <button
                  key={category}
                  ref={(el) => {
                    pillRefs.current[category] = el;
                  }}
                  type="button"
                  onClick={() => handlePillClick(category)}
                  className={`inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-all lg:text-[13px] ${
                    isActive
                      ? 'border-[#8B1538] bg-[#8B1538] text-white shadow-sm'
                      : 'border-[#1A2B3C]/15 bg-white/70 text-[#1A2B3C]/70 hover:border-[#8B1538]/40 hover:text-[#8B1538]'
                  }`}
                >
                  {label}
                  <span
                    className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${
                      isActive ? 'bg-white/25 text-white' : 'bg-[#1A2B3C]/[0.08] text-[#1A2B3C]/60'
                    }`}
                  >
                    {items.length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* CATEGORIES ====================================================== */}
      <section className="relative px-6 pb-20 pt-12 sm:pt-16 lg:px-12 lg:pb-28 lg:pt-20">
        <div className="relative mx-auto max-w-6xl">
          {grouped.length === 0 ? (
            <p className="py-12 text-center text-base text-[#1A2B3C]/60">
              {seite?.noEntriesText || 'Aktuell sind keine Fragen hinterlegt.'}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[14rem_1fr] lg:gap-14">
              {/* SIDEBAR (lg+) - sticky vertical category nav */}
              {grouped.length > 1 && (
                <aside className="hidden lg:block">
                  <nav
                    aria-label="FAQ Kategorien"
                    className="sticky top-28 border-l border-[#1A2B3C]/10 pl-5"
                  >
                    <p className="mb-5 text-xs font-bold uppercase tracking-[0.25em] text-[#1A2B3C]/45 lg:text-[13px]">
                      Kategorien
                    </p>
                    <ul className="space-y-1">
                      {grouped.map(({ category, label, items }) => {
                        const isActive = activeCategory === category;
                        return (
                          <li key={category}>
                            <button
                              type="button"
                              onClick={() => handlePillClick(category)}
                              className={`group relative flex w-full items-center justify-between gap-3 rounded-md py-2 pl-1 pr-2 text-left text-sm leading-snug transition-colors lg:text-[15px] ${
                                isActive
                                  ? 'font-semibold text-[#8B1538]'
                                  : 'font-medium text-[#1A2B3C]/65 hover:text-[#8B1538]'
                              }`}
                            >
                              <span
                                aria-hidden
                                className={`absolute -left-[21px] top-1/2 h-5 w-[2px] -translate-y-1/2 rounded-full transition-all ${
                                  isActive ? 'bg-[#8B1538]' : 'bg-transparent'
                                }`}
                              />
                              <span>{label}</span>
                              <span
                                className={`text-[10px] font-bold tabular-nums tracking-wider transition-colors ${
                                  isActive ? 'text-[#C9A961]' : 'text-[#1A2B3C]/35 group-hover:text-[#1A2B3C]/55'
                                }`}
                              >
                                {String(items.length).padStart(2, '0')}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                </aside>
              )}

              {/* CONTENT */}
              <div className="space-y-16 lg:space-y-24">
                {grouped.map(({ category, label, items }) => (
                  <div key={category} id={categorySectionId(category)} className="scroll-mt-32 lg:scroll-mt-28">
                    <RevealStagger className="mb-8 flex items-center gap-4 lg:mb-10">
                      <span className="h-px flex-shrink-0 w-10 bg-[#C9A961]" />
                      <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#8B1538] lg:text-[13px]">
                        {label}
                      </span>
                      <span className="h-px flex-1 bg-[#1A2B3C]/[0.08]" />
                    </RevealStagger>

                    <RevealStagger className="grid grid-cols-1 gap-5 lg:gap-6">
                      {items.map((faq, idx) => (
                        <FaqCard
                          key={faq.id}
                          number={idx + 1}
                          question={faq.question}
                          answer={faq.answer}
                          variant="page"
                        />
                      ))}
                    </RevealStagger>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
}
